"use client";

import {
  Eye,
  Search,
  Filter,
  ChevronDown,
  FileText,
  User,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { useRouter } from "next/navigation";
import { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";

// Imported per-module rather than through the "@/components/common" barrel:
// that barrel also re-exports RichTextEditor, which drags CodeMirror (vim.js,
// sqf.js, purify, token.js) into this page bundle for nothing.
import { Header } from "@/components/common/Header";
import { Footer } from "@/components/common/Footer";
import { AppFAQSection } from "@/components/custom/shared/AppFAQSection";
import { blogsFaqs } from "./faqs";
import blogCacheManager from "@/lib/blogCache";
import { authorName } from "@/lib/authors";

// initialBlogs/initialPagination/initialTags are server-fetched in page.jsx
// so the first page of articles is in the server-rendered HTML (SEO/AI
// crawlers see real links instead of an empty loading state).
const BlogsPageClient = ({ initialBlogs, initialPagination, initialTags }) => {
  const hasInitialData = Array.isArray(initialBlogs) && initialBlogs.length > 0;
  const [blogs, setBlogs] = useState(initialBlogs || []);
  const [loading, setLoading] = useState(!hasInitialData);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTag, setSelectedTag] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(initialPagination || {});
  const [allTags, setAllTags] = useState(initialTags || []);
  const [isAdminPreview, setIsAdminPreview] = useState(false);
  const [activeTab, setActiveTab] = useState("blogs");
  const router = useRouter();
  const searchTimeoutRef = useRef(null);
  const skipInitialFetchRef = useRef(hasInitialData);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      setIsAdminPreview(urlParams.get("admin_preview") === "true");
    }
  }, []);

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);

      const cached = blogCacheManager.getCachedBlogList(
        currentPage,
        searchTerm,
        selectedTag,
        isAdminPreview,
        "en-GB"
      );

      if (cached) {
        setBlogs(cached.data);
        setPagination(cached.pagination);
        setAllTags(cached.tags);
        setLoading(false);
        return;
      }

      const result = await blogCacheManager.prefetchBlogList(
        currentPage,
        searchTerm,
        selectedTag,
        isAdminPreview,
        "en-GB"
      );

      if (result) {
        setBlogs(result.data);
        setPagination(result.pagination);
        setAllTags(result.tags);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, selectedTag, isAdminPreview]);

  useEffect(() => {
    // First render already has server-fetched data; skip the duplicate fetch.
    if (skipInitialFetchRef.current) {
      skipInitialFetchRef.current = false;
      return;
    }
    fetchBlogs();
  }, [fetchBlogs]);

  // Cleanup search timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  // Debounced search function
  const debouncedSearch = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
    }, 500);
  }, []);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleTagFilter = (tag) => {
    setSelectedTag(tag === selectedTag ? "" : tag);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const truncateText = (text, maxWords = 20) => {
    const words = text.split(" ");
    if (words.length <= maxWords) {
      return text;
    }
    return `${words.slice(0, maxWords).join(" ")}...`;
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const calculateReadTime = (text) => {
    const wordsPerMinute = 200;
    const words = text.replace(/<[^>]*>/g, "").split(" ").length;
    return Math.ceil(words / wordsPerMinute);
  };

  return (
    <div className="min-h-screen bg-[#212121]">
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* Hero Section */}
      <section className="relative bg-[#212121] pt-28 pb-8 overflow-hidden">
        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            className="text-center max-w-4xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-4xl lg:text-5xl font-medium tracking-tight text-[#F8F7F4] mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Discover stories that inspire growth
            </motion.h1>

            <motion.p
              className="text-lg font-medium leading-relaxed max-w-4xl mx-auto text-[#A4A4A4] mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              Expert insights, industry trends, and success stories that drive
              business growth and customer loyalty.
            </motion.p>

            {/* Admin Preview Notice */}
            {isAdminPreview && (
              <motion.div
                className="inline-flex items-center px-6 py-3 bg-yellow-500/20 border-2 border-yellow-500/50 rounded-lg text-yellow-200 text-sm font-bold tracking-wide uppercase mb-8"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
              >
                <Eye className="w-4 h-4 mr-2" />
                Admin Preview Mode - All Posts Visible
              </motion.div>
            )}

            {/* Search Bar */}
            <motion.div
              className="max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <form onSubmit={handleSearch} className="relative">
                <div className="flex bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                  <div className="flex-1 relative">
                    <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                    <input
                      type="search"
                      // A placeholder is not an accessible name; it disappears
                      // on focus and agents cannot rely on it.
                      aria-label="Search articles"
                      placeholder="Search articles..."
                      value={searchTerm}
                      onChange={handleSearchInputChange}
                      className="w-full pl-14 pr-6 py-5 text-white placeholder-white/40 bg-transparent border-none outline-none text-lg font-semibold"
                    />
                  </div>
                  <button
                    type="submit"
                    className="px-8 py-5 bg-white/10 hover:bg-white/15 text-white font-bold transition-all duration-300"
                  >
                    Search
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-6 pt-8 pb-16">
        {/* Category Filter */}
        {allTags?.length > 0 && (
          <motion.div
            className="mb-12 max-w-md mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative">
              <Filter className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#909090]" />
              <select
                // Its only visible affordance is the adjacent filter icon, so
                // screen readers and agent accessibility trees saw an unnamed
                // combobox.
                aria-label="Filter articles by topic"
                value={selectedTag}
                onChange={(e) => handleTagFilter(e.target.value)}
                className="w-full pl-12 pr-10 py-4 bg-[#1E1E1E] rounded-xl border-2 border-white/10 text-[#F8F7F4] font-semibold appearance-none cursor-pointer hover:border-white/20 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/20"
                style={{
                  backgroundImage: "none",
                  WebkitAppearance: "none",
                  MozAppearance: "none",
                }}
              >
                <option value="" className="bg-[#1E1E1E] text-[#F8F7F4]">
                  Browse by Category
                </option>
                {allTags.map((tag) => (
                  <option
                    key={tag}
                    value={tag}
                    className="bg-[#1E1E1E] text-[#F8F7F4]"
                  >
                    {tag}
                  </option>
                ))}
              </select>
              <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronDown className="w-5 h-5 text-[#909090] transition-transform duration-200" />
              </div>
            </div>
          </motion.div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="py-16">
            {" "}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-[#1E1E1E] rounded-2xl overflow-hidden animate-pulse border-2 border-white/10"
                >
                  <div className="h-64 bg-[#2a2a2a]"></div>
                  <div className="p-6">
                    <div className="h-4 bg-[#2a2a2a] rounded mb-4"></div>
                    <div className="h-6 bg-[#2a2a2a] rounded mb-3"></div>
                    <div className="h-4 bg-[#2a2a2a] rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-[#2a2a2a] rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Blog Grid */}
        {!loading && (
          <>
            {blogs.length === 0 ? (
              <div className="text-center py-20">
                <FileText className="w-12 h-12 text-[#909090] mx-auto mb-6" />
                <h3 className="text-3xl font-medium text-[#F8F7F4] mb-4 tracking-tight">
                  No articles found
                </h3>
                <p className="text-xl text-[#A4A4A4] mb-6 max-w-md mx-auto font-semibold">
                  {searchTerm || selectedTag
                    ? "Try adjusting your search criteria or filters."
                    : "Check back soon for new insights and stories."}
                </p>
                {(searchTerm || selectedTag) && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedTag("");
                      setCurrentPage(1);
                    }}
                    className="px-8 py-4 bg-white/10 text-white rounded-lg hover:bg-white/15 transition-all duration-300 font-bold border border-white/10"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, staggerChildren: 0.1 }}
              >
                {blogs.map((blog, index) => (
                  <motion.article
                    key={blog.id}
                    className="bg-[#1E1E1E] rounded-2xl overflow-hidden hover:border-white/20 transition-all duration-500 transform hover:-translate-y-1 group border-2 border-white/10 cursor-pointer"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1, duration: 0.6 }}
                    onClick={() => {
                      if (typeof window !== "undefined") {
                        sessionStorage.setItem(
                          "currentBlog",
                          JSON.stringify({
                            ...blog,
                            timestamp: Date.now(),
                          })
                        );
                      }
                      router.push(
                        `/blogs/${blog.slug}${
                          isAdminPreview ? "?admin_preview=true" : ""
                        }`
                      );
                    }}
                  >
                    {/* Featured Image */}
                    <div className="relative h-64 overflow-hidden">
                      {blog.thumbnailImage ? (
                        <Image
                          src={blog.thumbnailImage}
                          alt={blog.title}
                          fill
                          // `fill` defaults `sizes` to 100vw, so a 359px card
                          // was downloading a full-viewport image. The grid is
                          // 1/2/3 columns; describing that cuts the bytes to
                          // roughly a third on desktop.
                          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          // The first card is above the fold and holds the LCP
                          // element, which was being lazy-loaded and only
                          // discovered after hydration — 0.8s of dead time.
                          // Only that one is preloaded: the next two are also
                          // above the fold but preloading them just splits the
                          // bandwidth the LCP element is waiting on, and
                          // `fetchPriority` is what puts the priority hint on
                          // the preload itself rather than only on the <img>.
                          priority={index === 0}
                          fetchPriority={index === 0 ? "high" : undefined}
                          // (`loading="eager"` on the rest of the row is not the
                          // answer either — Next emits a preload for every
                          // non-lazy image, which is the thing being avoided.)
                          className="object-cover group-hover:scale-110 transition-transform duration-500"
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                            e.currentTarget.nextSibling.style.display = "flex";
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-full h-full bg-[#2a2a2a] flex items-center justify-center ${
                          blog.thumbnailImage ? "hidden" : "flex"
                        }`}
                      >
                        <FileText className="w-16 h-16 text-[#909090]" />
                      </div>

                      {/* Tags overlay */}
                      <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                        {isAdminPreview && blog.visibility === "hidden" && (
                          <span className="px-3 py-1 bg-red-500 text-white text-xs font-semibold rounded-full">
                            HIDDEN
                          </span>
                        )}
                        {blog.tags?.slice(0, 2).map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 bg-white/10 text-white/70 text-xs font-bold rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Reading time overlay */}
                      <div className="absolute bottom-4 right-4">
                        <div className="flex items-center space-x-1 px-3 py-1 bg-white/10 text-white/70 text-xs font-bold rounded-full">
                          <Clock className="w-3 h-3" />
                          <span>
                            {blog.readTime ?? calculateReadTime(blog.content ?? "")} min read
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6">
                      {/* Author and Date - Top Row */}
                      <div className="flex items-center justify-between mb-4 text-sm">
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-[#909090]" />
                          <span className="font-semibold text-[#A4A4A4]">
                            {authorName(blog.author?.username) || "Keystone Team"}
                          </span>
                        </div>

                        <div className="flex items-center space-x-1 text-[#909090] font-medium">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(blog.createdAt)}</span>
                        </div>
                      </div>

                      {/* Title — real anchor so the article is crawlable;
                          card onClick handles the rest of the surface */}
                      <h2 className="text-xl font-medium text-[#F8F7F4] mb-3 leading-tight line-clamp-2 group-hover:text-white transition-all duration-300 tracking-tight">
                        <Link
                          href={`/blogs/${blog.slug}${
                            isAdminPreview ? "?admin_preview=true" : ""
                          }`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {blog.title}
                        </Link>
                      </h2>

                      {/* Excerpt */}
                      <p className="text-[#A4A4A4] leading-relaxed line-clamp-3 font-medium">
                        {truncateText(blog.excerpt)}
                      </p>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-2">
                <button
                  onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-6 py-3 bg-[#1E1E1E] border-2 border-white/10 text-[#A4A4A4] rounded-lg hover:border-white/20 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous
                </button>

                {Array.from(
                  { length: Math.min(5, pagination.pages) },
                  (_, i) => {
                    const page = currentPage <= 3 ? i + 1 : currentPage - 2 + i;
                    if (page > pagination.pages) return null;

                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-3 font-bold rounded-lg transition-all duration-300 border-2 ${
                          currentPage === page
                            ? "bg-white/15 text-white border-white/20"
                            : "bg-[#1E1E1E] border-white/10 text-[#A4A4A4] hover:border-white/20 hover:text-white"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  }
                )}

                <button
                  onClick={() =>
                    handlePageChange(
                      Math.min(currentPage + 1, pagination.pages)
                    )
                  }
                  disabled={currentPage === pagination.pages}
                  className="flex items-center px-6 py-3 bg-[#1E1E1E] border-2 border-white/10 text-[#A4A4A4] rounded-lg hover:border-white/20 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-bold"
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            )}

            {/* The pagination above is onClick-driven, so a crawler that
                cannot run JS sees only this first page of nine. This anchor is
                the path to the other hundred — until now /blogs/archive was
                reachable from the footer alone. */}
            <p className="mt-12 text-center text-[#A4A4A4]">
              Looking for something older?{" "}
              <Link
                href="/blogs/archive"
                className="text-[#F8F7F4] underline underline-offset-4 decoration-white/30 hover:decoration-white transition-colors"
              >
                Browse all {pagination.total || ""} articles by topic
              </Link>
              .
            </p>
          </>
        )}
      </main>

      {/* Question-phrased h2 + h3s, mirrored as FAQPage JSON-LD in page.jsx */}
      <AppFAQSection
        title="What should you know about the Keystone blog?"
        faqs={blogsFaqs}
      />

      <Footer />
    </div>
  );
};

export default BlogsPageClient;
